import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  GroupAdd as GroupAddIcon,
} from '@mui/icons-material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { http } from '../../../../api/http';
import { useConceptoStore } from '../store';
import EditConcepto from './EditConcepto';

const tipoColors: Record<string, 'success' | 'error' | 'info'> = {
  R: 'success',
  D: 'error',
  A: 'info',
};

const tipoLabels: Record<string, string> = {
  R: 'Remuneración',
  D: 'Descuento',
  A: 'Aporte',
};

const ConceptosPage: React.FC = () => {
  const {
    conceptos,
    loading,
    dialogOpen,
    selectedConcepto,
    filterTipo,
    fetchConceptos,
    deleteConcepto,
    setSelectedConcepto,
    setFilterTipo,
    openDialog,
    closeDialog,
  } = useConceptoStore();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [masiveDialog, setMasiveDialog] = useState(false);
  const [masiveConcepto, setMasiveConcepto] = useState<number>(0);
  const [masiveMontoFijo, setMasiveMontoFijo] = useState<string>('');
  const [masivePorcentaje, setMasivePorcentaje] = useState<string>('');
  const [masiveLoading, setMasiveLoading] = useState(false);
  const [masiveResult, setMasiveResult] = useState<string | null>(null);

  useEffect(() => {
    fetchConceptos();
  }, [fetchConceptos]);

  const handleOpenMasive = () => {
    setMasiveConcepto(0);
    setMasiveMontoFijo('');
    setMasivePorcentaje('');
    setMasiveResult(null);
    setMasiveDialog(true);
  };

  const handleSaveMasive = async () => {
    if (!masiveConcepto) return;
    setMasiveLoading(true);
    setMasiveResult(null);
    try {
      const workersResp = await http.get('/planilla/trabajador');
      const workers = workersResp.data;
      const ids = workers.map((w: any) => w.idTrabajador);
      const resp = await http.post('/planilla/trabajador/asignacion-masiva', {
        idConcepto: masiveConcepto,
        idTrabajadores: ids,
        montoFijo: masiveMontoFijo ? Number(masiveMontoFijo) : null,
        porcentaje: masivePorcentaje ? Number(masivePorcentaje) : null,
        activo: true,
      });
      const count = resp.data?.asignados ?? resp.data?.length ?? ids.length;
      setMasiveResult(`Asignacion completada: ${count} trabajador(es) asignados.`);
    } catch {
      setMasiveResult('Error al realizar la asignacion masiva.');
    } finally {
      setMasiveLoading(false);
    }
  };

  const filteredConceptos = filterTipo
    ? conceptos.filter(c => c.tipoConcepto === filterTipo)
    : conceptos;

  const handleEdit = (id: number) => {
    const concepto = conceptos.find((c) => c.idConcepto === id);
    if (concepto) {
      setSelectedConcepto(concepto);
      openDialog();
    }
  };

  const handleCreate = () => {
    setSelectedConcepto(null);
    openDialog();
  };

  const handleDelete = async (id: number) => {
    const concepto = conceptos.find(c => c.idConcepto === id);
    if (concepto?.esConceptoSistema) {
      alert('No se puede eliminar un concepto del sistema');
      return;
    }
    if (window.confirm('¿Está seguro de eliminar este concepto?')) {
      await deleteConcepto(id);
    }
  };

  const columns: GridColDef[] = [
    { field: 'codigo', headerName: 'Código', width: 100 },
    { field: 'descripcion', headerName: 'Descripción', flex: 1, minWidth: 250 },
    {
      field: 'tipoConcepto',
      headerName: 'Tipo',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={tipoLabels[params.value] || params.value}
          size="small"
          color={tipoColors[params.value] || 'default'}
          variant="outlined"
        />
      ),
    },
    { field: 'tipoCalculo', headerName: 'Cálculo', width: 110 },
    { field: 'codigoPDT', headerName: 'PDT', width: 80 },
    {
      field: 'porcentajeBase',
      headerName: '% Base',
      width: 90,
      valueFormatter: (value: number | null) => value != null ? `${value}%` : '',
    },
    {
      field: 'formula',
      headerName: 'Fórmula',
      width: 250,
      valueGetter: (_value, row) => row.formula || '',
    },
    { field: 'orden', headerName: 'Orden', width: 70, type: 'number' },
    {
      field: 'activo',
      headerName: 'Estado',
      width: 90,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Activo' : 'Inactivo'}
          size="small"
          color={params.value ? 'success' : 'default'}
          variant="filled"
          sx={{ fontSize: '0.7rem' }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => handleEdit(params.row.idConcepto)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {!params.row.esConceptoSistema && (
            <Tooltip title="Eliminar">
              <IconButton size="small" color="error" onClick={() => handleDelete(params.row.idConcepto)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 3, gap: { xs: 1.5, sm: 0 } }}>
        <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold">
          Conceptos de Planilla
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" color="secondary" size={isMobile ? 'small' : 'medium'} startIcon={<GroupAddIcon />} onClick={handleOpenMasive}>
            {isMobile ? 'Masiva' : 'Asignacion Masiva'}
          </Button>
          <Button variant="contained" size={isMobile ? 'small' : 'medium'} startIcon={<AddIcon />} onClick={handleCreate}>
            {isMobile ? 'Nuevo' : 'Nuevo Concepto'}
          </Button>
        </Stack>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <ToggleButtonGroup
          value={filterTipo}
          exclusive
          onChange={(_, value) => setFilterTipo(value)}
          size="small"
        >
          <ToggleButton value={null as unknown as string}>Todos</ToggleButton>
          <ToggleButton value="R">Remuneraciones</ToggleButton>
          <ToggleButton value="D">Descuentos</ToggleButton>
          <ToggleButton value="A">Aportes</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary' }}>
          {filteredConceptos.length} concepto(s)
        </Typography>
      </Paper>

      <Paper sx={{ height: { xs: 'calc(100vh - 220px)', sm: 'calc(100vh - 260px)' } }}>
        <DataGrid
          rows={filteredConceptos}
          columns={columns}
          getRowId={(row) => row.idConcepto}
          loading={loading}
          density={isMobile ? 'compact' : 'standard'}
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: { sortModel: [{ field: 'orden', sort: 'asc' }] },
          }}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8fafc',
            },
          }}
        />
      </Paper>

      <EditConcepto
        open={dialogOpen}
        concepto={selectedConcepto}
        onClose={closeDialog}
        fullScreen={isMobile}
      />

      <Dialog open={masiveDialog} onClose={() => setMasiveDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Asignacion Masiva de Concepto</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Concepto</InputLabel>
              <Select
                value={masiveConcepto || ''}
                label="Concepto"
                onChange={(e) => setMasiveConcepto(Number(e.target.value))}
              >
                {conceptos.filter(c => c.activo).map(c => (
                  <MenuItem key={c.idConcepto} value={c.idConcepto}>
                    {c.codigo} - {c.descripcion}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Monto Fijo"
              type="number"
              size="small"
              fullWidth
              value={masiveMontoFijo}
              onChange={(e) => setMasiveMontoFijo(e.target.value)}
            />
            <TextField
              label="Porcentaje"
              type="number"
              size="small"
              fullWidth
              value={masivePorcentaje}
              onChange={(e) => setMasivePorcentaje(e.target.value)}
            />
            <Alert severity="info" variant="outlined">
              Se asignara a todos los trabajadores activos.
            </Alert>
            {masiveResult && (
              <Alert severity={masiveResult.startsWith('Error') ? 'error' : 'success'}>
                {masiveResult}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMasiveDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSaveMasive}
            disabled={masiveLoading || !masiveConcepto}
            startIcon={masiveLoading ? <CircularProgress size={18} /> : <GroupAddIcon />}
          >
            Asignar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConceptosPage;
